/**
 * Status Effect System for Kobayashi Maru
 * Processes burning, slowed, drained, and disabled status effects
 */
import { query, removeComponent, addComponent, hasComponent } from 'bitecs';
import type { GameWorld } from '../ecs/world';
import {
    BurningStatus,
    SlowedStatus,
    DrainedStatus,
    DisabledStatus,
    Health,
    Velocity,
    AIBehavior
} from '../ecs/components';
import { EventBus } from '../core/EventBus';
import { GameEventType } from '../types/events';
import { COMBAT_CONFIG } from '../config';

/**
 * Process all status effects
 */
export function statusEffectSystem(world: GameWorld, deltaTime: number): void {
    processBurning(world, deltaTime);
    processSlowed(world, deltaTime);
    processDrained(world, deltaTime);
    processDisabled(world, deltaTime);
}

/**
 * Process burning status - Apply DOT damage
 */
function processBurning(world: GameWorld, deltaTime: number): void {
    const entities = query(world, [BurningStatus, Health, AIBehavior]);

    for (let i = 0; i < entities.length; i++) {
        const eid = entities[i];

        // Update tick timer
        BurningStatus.lastTickTime[eid] += deltaTime;

        // Check if it's time for next tick
        if (BurningStatus.lastTickTime[eid] >= BurningStatus.tickInterval[eid]) {
            // Apply damage
            const damage = BurningStatus.damagePerTick[eid];
            Health.current[eid] = Math.max(0, Health.current[eid] - damage);

            // Decrement ticks
            BurningStatus.ticksRemaining[eid]--;
            BurningStatus.lastTickTime[eid] = 0;

            // Remove if expired
            if (BurningStatus.ticksRemaining[eid] <= 0) {
                removeComponent(world, eid, BurningStatus);
                EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_REMOVED, {
                    entityId: eid, effectType: 1
                });
            }
        }
    }
}

/**
 * Process slowed status - Reduce movement speed
 */
function processSlowed(world: GameWorld, deltaTime: number): void {
    const entities = query(world, [SlowedStatus, Velocity, AIBehavior]);

    for (let i = 0; i < entities.length; i++) {
        const eid = entities[i];

        // Decrement duration
        SlowedStatus.duration[eid] -= deltaTime;

        // Remove if expired
        if (SlowedStatus.duration[eid] <= 0) {
            // Restore original speed
            const originalSpeed = SlowedStatus.originalSpeed[eid];
            const currentSpeed = Math.sqrt(Velocity.x[eid] ** 2 + Velocity.y[eid] ** 2);

            if (currentSpeed > 0) {
                const multiplier = originalSpeed / currentSpeed;
                Velocity.x[eid] *= multiplier;
                Velocity.y[eid] *= multiplier;
            }

            removeComponent(world, eid, SlowedStatus);
            EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_REMOVED, {
                entityId: eid, effectType: 2
            });
        }
    }
}

/**
 * Process drained status - Stacking speed reduction
 */
function processDrained(world: GameWorld, deltaTime: number): void {
    const entities = query(world, [DrainedStatus, Velocity, AIBehavior]);

    for (let i = 0; i < entities.length; i++) {
        const eid = entities[i];

        // Decrement duration
        DrainedStatus.duration[eid] -= deltaTime;

        // Remove if expired
        if (DrainedStatus.duration[eid] <= 0) {
            // Reduce stacks
            DrainedStatus.stacks[eid]--;

            if (DrainedStatus.stacks[eid] <= 0) {
                removeComponent(world, eid, DrainedStatus);
                EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_REMOVED, {
                    entityId: eid, effectType: 3
                });
            } else {
                // Reset duration for remaining stacks
                DrainedStatus.duration[eid] = COMBAT_CONFIG.STATUS_EFFECTS.DRAIN.DURATION; // 3 second duration per stack
            }
        }
    }
}

/**
 * Process disabled status - Systems offline
 */
function processDisabled(world: GameWorld, deltaTime: number): void {
    const entities = query(world, [DisabledStatus, AIBehavior]);

    for (let i = 0; i < entities.length; i++) {
        const eid = entities[i];

        // Decrement duration
        DisabledStatus.duration[eid] -= deltaTime;

        // Remove if expired
        if (DisabledStatus.duration[eid] <= 0) {
            removeComponent(world, eid, DisabledStatus);
            EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_REMOVED, {
                entityId: eid, effectType: 4
            });
        }
    }
}

/**
 * Apply burning status to an entity
 */
export function applyBurning(
    world: GameWorld,
    eid: number,
    damagePerTick: number,
    duration: number
): void {
    const tickInterval = COMBAT_CONFIG.STATUS_EFFECTS.BURNING.TICK_INTERVAL; // 1 second per tick
    const ticks = Math.floor(duration / tickInterval);

    // Add component if not already present
    if (!hasComponent(world, eid, BurningStatus)) {
        addComponent(world, eid, BurningStatus);
    }

    BurningStatus.damagePerTick[eid] = damagePerTick;
    BurningStatus.ticksRemaining[eid] = ticks;
    BurningStatus.tickInterval[eid] = tickInterval;
    BurningStatus.lastTickTime[eid] = 0;

    EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_APPLIED, {
        entityId: eid, effectType: 1, duration
    });
}

/**
 * Apply slowed status to an entity
 */
export function applySlowed(
    world: GameWorld,
    eid: number,
    slowPercent: number,
    duration: number
): void {
    // Add component if not already present
    if (!hasComponent(world, eid, SlowedStatus)) {
        addComponent(world, eid, SlowedStatus);

        // Store original speed only on first application
        const currentSpeed = Math.sqrt(
            Velocity.x[eid] ** 2 + Velocity.y[eid] ** 2
        );
        SlowedStatus.originalSpeed[eid] = currentSpeed;
    }

    SlowedStatus.slowPercent[eid] = slowPercent;
    SlowedStatus.duration[eid] = duration;

    // Apply slow to velocity
    const multiplier = 1 - slowPercent;
    Velocity.x[eid] *= multiplier;
    Velocity.y[eid] *= multiplier;

    EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_APPLIED, {
        entityId: eid, effectType: 2, duration
    });
}

/**
 * Apply drained status to an entity (stacking)
 */
export function applyDrained(
    world: GameWorld,
    eid: number,
    duration: number
): void {
    const maxStacks = COMBAT_CONFIG.STATUS_EFFECTS.DRAIN.MAX_STACKS;

    // Add component if not already present
    if (!hasComponent(world, eid, DrainedStatus)) {
        addComponent(world, eid, DrainedStatus);
        DrainedStatus.stacks[eid] = 0;
    }

    const currentStacks = DrainedStatus.stacks[eid];
    DrainedStatus.stacks[eid] = Math.min(currentStacks + 1, maxStacks);
    DrainedStatus.duration[eid] = duration;

    // Apply speed reduction (10% per stack)
    const slowAmount = COMBAT_CONFIG.STATUS_EFFECTS.DRAIN.SLOW_PER_STACK;
    Velocity.x[eid] *= (1 - slowAmount);
    Velocity.y[eid] *= (1 - slowAmount);

    EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_APPLIED, {
        entityId: eid, effectType: 3, duration
    });
}

/**
 * Apply disabled status to an entity
 */
export function applyDisabled(
    world: GameWorld,
    eid: number,
    duration: number,
    systems: number = 1 // Default: weapons only
): void {
    // Add component if not already present
    if (!hasComponent(world, eid, DisabledStatus)) {
        addComponent(world, eid, DisabledStatus);
    }

    DisabledStatus.duration[eid] = duration;
    DisabledStatus.disabledSystems[eid] = systems;

    EventBus.getInstance().emit(GameEventType.STATUS_EFFECT_APPLIED, {
        entityId: eid, effectType: 4, duration
    });
}
